import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("As senhas não coincidem.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Senha redefinida com sucesso!");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fafaf8] p-6">
      <div className="w-full max-w-md">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 mb-8 mx-auto" data-testid="reset-logo">
          <span className="w-9 h-9 rounded-xl bg-[#1b5e3f] flex items-center justify-center"><MapPin className="w-5 h-5 text-white" /></span>
          <span className="font-head text-xl font-bold">Brasileiros<span className="text-[#f59e0b]">.PT</span></span>
        </button>

        {done ? (
          <div className="bg-white border border-[#e7e5e4] rounded-2xl p-8 text-center" data-testid="reset-success">
            <CheckCircle2 className="w-12 h-12 text-[#1b5e3f] mx-auto mb-4" />
            <h1 className="font-head text-2xl font-bold text-[#1c1917]">Senha redefinida!</h1>
            <p className="text-[#78716c] mt-2">Agora você já pode entrar com a sua nova senha.</p>
            <Button data-testid="reset-go-login-btn" onClick={() => navigate("/login")} className="w-full mt-6 rounded-xl bg-[#1b5e3f] hover:bg-[#13422c] py-6 text-base">
              Ir para o login
            </Button>
          </div>
        ) : !token ? (
          <div className="bg-white border border-[#e7e5e4] rounded-2xl p-8 text-center" data-testid="reset-invalid">
            <h1 className="font-head text-2xl font-bold text-[#1c1917]">Link inválido</h1>
            <p className="text-[#78716c] mt-2">Este link de redefinição está incompleto. Solicite uma nova redefinição na tela de login.</p>
            <Button onClick={() => navigate("/login")} className="w-full mt-6 rounded-xl bg-[#1b5e3f] hover:bg-[#13422c] py-6 text-base">
              Voltar ao login
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-[#e7e5e4] rounded-2xl p-8">
            <h1 className="font-head text-2xl sm:text-3xl font-bold text-[#1c1917]">Criar nova senha</h1>
            <p className="text-[#78716c] mt-2">Digite a sua nova senha abaixo.</p>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div>
                <label className="text-sm font-medium text-[#1c1917]">Nova senha</label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716c]" />
                  <input data-testid="reset-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" className="w-full bg-white border border-[#e7e5e4] rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f] transition-all" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1c1917]">Confirmar nova senha</label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716c]" />
                  <input data-testid="reset-confirm-input" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repita a senha" className="w-full bg-white border border-[#e7e5e4] rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f] transition-all" />
                </div>
              </div>

              {error && <p data-testid="reset-error" className="text-sm text-[#ef4444] bg-red-50 rounded-lg px-3 py-2">{error}</p>}

              <Button data-testid="reset-submit-btn" type="submit" disabled={loading} className="w-full rounded-xl bg-[#1b5e3f] hover:bg-[#13422c] py-6 text-base">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Redefinir senha"}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
